import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { ShareBrief, ShareBriefModal } from "./ShareBrief";
import { api, errText, media } from "../../lib/api";
import { ZoomImage } from "../../components/ZoomImage";
import { AddToNewsletter } from "./NewsletterPage";
import { Badge, CustomerLink, Drawer, KeyRow, Panel, ShopLink, TabBar, eurOf, money, shortDate, stamp, statusTone, useEurRate } from "./ui";

const TABS = [["summary", "Summary"], ["payment", "Payment"], ["timeline", "Timeline"], ["messages", "Messages"]];

// Awaiting-acceptance orders: keep waiting or hand to a ranked secondary partner of the city.
const HandOverPanel = ({ orderId, overdue, act }) => {
  const [opts, setOpts] = useState(null);
  const [pick, setPick] = useState("");
  useEffect(() => {
    api.get(`/admin/orders/${orderId}/partners`).then((r) => setOpts(r.data)).catch(() => setOpts({ options: [] }));
  }, [orderId]);
  const available = (opts?.options || []).filter((p) => !p.paused);
  return (
    <Panel title={overdue ? "Awaiting acceptance — overdue" : "Awaiting acceptance"} testid="order-drawer-handover">
      <p className={`text-xs ${overdue ? "text-danger" : "text-muted"}`}>
        {overdue ? "One working hour has passed without acceptance. Keep waiting, or hand the order to a secondary florist."
                 : "The partner has a one-tap Accept link. The one-hour clock only runs during their working hours."}
      </p>
      {opts && !available.length ? <p className="text-xs text-warning" data-testid="order-drawer-no-secondary">No secondary available in this city.</p> : null}
      {available.length ? (
        <div className="flex flex-wrap gap-2 items-center">
          <select className="ctl" data-testid="order-drawer-secondary-select" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">Choose a secondary…</option>
            {available.map((p) => <option key={p.id} value={p.id}>{p.shop_name}{p.is_primary ? " (primary)" : ` · rank ${p.rank}`}</option>)}
          </select>
          <button className="btn-primary text-[11px] px-3" disabled={!pick} data-testid="order-drawer-handover-button"
                  onClick={() => act(() => api.post(`/admin/orders/${orderId}/reassign`, { florist_id: pick, reason: overdue ? "acceptance overdue" : "ops hand-over" }), "Handed to the secondary florist — both partners alerted, clock restarted.")}>
            Hand to secondary
          </button>
        </div>
      ) : null}
    </Panel>
  );
};

// Money is an admin-only lever: charge the hold, let it go, or give it back in full or in part.
const MoneyActions = ({ o, act }) => {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const state = o.payment?.payment_state;
  const totalMad = Math.round(o.payment?.refundable_mad || o.money?.total_mad || 0);
  const refunded = Math.round(o.money?.refund_mad || 0);
  const remaining = Math.max(totalMad - refunded, 0);
  return (
    <Panel title="Money actions (admin only)" testid="order-drawer-money-actions">
      <p className="text-xs text-muted">
        {state === "authorized"
          ? "The card is held, not charged. It is captured automatically the moment the partner accepts — charge it here only if you need to go first."
          : state === "captured"
            ? `Charged in full. ${refunded ? `${refunded} MAD already given back · ${remaining} MAD left.` : "Nothing refunded yet."}`
            : state === "released" ? "The hold was released — the buyer was never charged."
              : "No card hold on this order."}
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        {state === "authorized" ? (
          <>
            <button className="btn-primary text-[11px] px-3" data-testid="order-capture"
                    onClick={() => act(() => api.post(`/admin/orders/${o.id}/capture`), "Card charged.")}>
              Charge the card now
            </button>
            <button className="btn-ghost text-[11px]" data-testid="order-release"
                    onClick={() => act(() => api.post(`/admin/orders/${o.id}/release`, { note: reason || "released by ops" }), "Hold released — nothing charged.")}>
              Release the hold
            </button>
          </>
        ) : null}
        {state === "captured" && remaining > 0 ? (
          <>
            <input className="field w-28" inputMode="decimal" placeholder={`MAD ≤ ${remaining}`} value={amount}
                   data-testid="order-refund-amount" onChange={(e) => setAmount(e.target.value)} />
            <button className="btn-ghost text-[11px]" data-testid="order-refund-partial" disabled={!Number(amount)}
                    onClick={() => act(() => api.post(`/admin/orders/${o.id}/refund`, { amount_mad: Number(amount), reason: reason || "partial refund by ops" }), "Partial refund sent.")}>
              Refund this amount
            </button>
            <button className="btn-ghost text-[11px]" data-testid="order-refund-full"
                    onClick={() => act(() => api.post(`/admin/orders/${o.id}/refund`, { reason: reason || "full refund by ops" }), "Full refund sent.")}>
              Refund the rest ({remaining} MAD)
            </button>
          </>
        ) : null}
      </div>
      <input className="field" placeholder="Reason recorded on the order (optional)" value={reason}
             data-testid="order-money-reason" onChange={(e) => setReason(e.target.value)} />
    </Panel>
  );
};

// Ops can drive the order itself: accept for the partner, dispatch it, close it with the photo.
const FULFIL_STEPS = [["preparing", "Mark as being prepared"], ["ready", "Mark as ready"],
                      ["out_for_delivery", "Out for delivery"]];

const FulfilmentPanel = ({ o, act, reload, onAccepted }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const live = ["accepted", "preparing", "ready", "out_for_delivery"].includes(o.status);
  const done = ["delivered", "completed"].includes(o.status);

  const deliver = async (withPhoto) => {
    if (withPhoto && !file) return;
    if (!withPhoto && !note.trim()) {
      setErr("Write a short note saying how the delivery was confirmed.");
      return;
    }
    setBusy(true);
    setErr("");
    const fd = new FormData();
    if (withPhoto) fd.append("photo", file);
    fd.append("note", note);
    try {
      await api.post(`/admin/orders/${o.id}/deliver`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null);
      setNote("");
      await reload();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Delivery progress" testid="order-drawer-fulfilment">
      <p className="text-xs text-muted">
        {done ? (o.proof?.photo_urls?.length ? "Delivered — the buyer has the photo." : "Delivered — the buyer got the delivery e-mail.")
              : o.status === "awaiting_acceptance"
                ? "The partner has not accepted yet. You can accept on their behalf, which also charges the held card."
                : live ? "Move the order along. Dispatching notifies the buyer by e-mail and the recipient by WhatsApp or SMS."
                       : "This order is not in a deliverable state."}
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        {o.status === "awaiting_acceptance" ? (
          <button className="btn-primary text-[11px] px-3" data-testid="order-accept"
                  onClick={() => act(() => api.post(`/admin/orders/${o.id}/accept`), "Order accepted for the partner.")
                    .then((ok) => ok && onAccepted?.())}>
            Accept for the partner
          </button>
        ) : null}
        {live ? FULFIL_STEPS.filter(([s]) => s !== o.status).map(([status, label]) => (
          <button key={status} className="btn-ghost text-[11px]" data-testid={`order-progress-${status}`}
                  onClick={() => act(() => api.post(`/admin/orders/${o.id}/progress`, { status }),
                                     status === "out_for_delivery" ? "Out for delivery — buyer and recipient notified." : `Marked ${label.toLowerCase()}.`)}>
            {label}
          </button>
        )) : null}
      </div>
      {live ? (
        <div className="space-y-3 border-t border-brass-light pt-3">
          <div className="space-y-2" data-testid="order-deliver-photo-block">
            <p className="smallcaps">Mark delivered · with a door photo</p>
            <input type="file" accept="image/*" className="text-xs" data-testid="order-proof-file"
                   onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <button className="btn-primary text-[11px] px-3" disabled={!file || busy} data-testid="order-mark-delivered"
                    onClick={() => deliver(true)}>
              {busy && file ? "Sending…" : "Mark delivered & e-mail the photo"}
            </button>
          </div>
          <div className="space-y-2 border-t border-brass-light pt-3" data-testid="order-deliver-no-photo-block">
            <p className="smallcaps">{file ? "Note for the record (optional)" : "Mark delivered · without a photo"}</p>
            <input className="field" placeholder={file ? "Optional note kept on the order" : "How was the delivery confirmed? e.g. handed to the recipient, confirmed by phone"}
                   value={note} data-testid="order-proof-note" onChange={(e) => setNote(e.target.value)} />
            <button className="btn-primary text-[11px] px-3" disabled={!!file || busy} data-testid="order-mark-delivered-no-photo"
                    onClick={() => deliver(false)}>
              {busy && !file ? "Sending…" : "Delivered without photo"}
            </button>
            <p className="text-[11px] text-muted">
              The buyer gets the usual delivery e-mail (no photo) — your note stays on the order.
            </p>
          </div>
          {err ? <p className="text-xs text-danger" data-testid="order-proof-error">{err}</p> : null}
        </div>
      ) : null}
    </Panel>
  );
};

// Forward the delivery brief to a florist with nothing that names us.
const SharePanel = ({ orderId }) => (
  <Panel title="Send to the florist" testid="order-drawer-share">
    <ShareBrief orderId={orderId} testid="order-share" />
  </Panel>
);

export default function OrderDrawer({ orderId, onClose, onChanged }) {
  const [o, setO] = useState(null);
  const [tab, setTab] = useState("summary");
  const [shops, setShops] = useState([]);
  const [msg, setMsg] = useState("");
  const [note, setNote] = useState("");
  const [shareAfterAccept, setShareAfterAccept] = useState(false);
  const fx = useEurRate();

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${orderId}/full`);
      setO(data);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  useEffect(() => {
    setO(null);
    load();
    api.get("/admin/filters").then((r) => setShops(r.data.florists)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const act = async (fn, done) => {
    setMsg("");
    try {
      await fn();
      setMsg(done);
      await load();
      onChanged?.();
      return true;
    } catch (e) {
      setMsg(errText(e));
      return false;
    }
  };

  if (!o) return <Drawer testid="order-drawer" title="Order" onClose={onClose}><p className="text-sm text-muted">Loading…</p></Drawer>;
  const p = o.payment;

  return (
    <Drawer testid="order-drawer" title={o.order_number} onClose={onClose}
            subtitle={`${o.status_label} · ${o.city} · ${shortDate(o.delivery_date)} ${o.delivery_window}`}>
      {msg ? <p className="text-sm text-brass" data-testid="order-drawer-message">{msg}</p> : null}
      {shareAfterAccept ? (
        <ShareBriefModal orderId={o.id} orderNumber={o.order_number} accepted onClose={() => setShareAfterAccept(false)} />
      ) : null}
      <TabBar tabs={TABS} value={tab} onChange={setTab} testid="order-drawer-tabs" />

      {tab === "summary" ? (
        <div className="space-y-4" data-testid="order-drawer-summary">
          <FulfilmentPanel o={o} act={act} reload={async () => { await load(); onChanged?.(); }}
                           onAccepted={() => setShareAfterAccept(true)} />

          <Panel title="The gift">
            <div className="flex gap-3 flex-wrap">
              {o.items.map((i, idx) => (
                <div key={`${i.title}-${idx}`} className="flex gap-2 items-center text-sm">
                  {i.image ? (
                    <ZoomImage src={media(i.image)} alt={i.title} caption={`${i.title} · ${i.variant || ""}`.trim()}
                               className="w-12 aspect-[4/5] shrink-0" testid={`order-item-photo-${idx}`} />
                  ) : null}
                  <span>{i.quantity} × {i.title}</span>
                </div>
              ))}
            </div>
            <KeyRow label="Card message">{o.card_message || "—"}</KeyRow>
          </Panel>

          <Panel title="Recipient" testid="order-drawer-recipient">
            <KeyRow label="Name">{o.recipient_name}</KeyRow>
            <KeyRow label="Phone">{o.recipient_phone}</KeyRow>
            <KeyRow label="Address">{o.address}</KeyRow>
            <KeyRow label="Landmark">{o.landmark_note || "—"}</KeyRow>
            <KeyRow label="Pinned location">
              {o.delivery_location ? (
                <a data-testid="admin-order-map-link" target="_blank" rel="noreferrer"
                   className="text-ink underline underline-offset-2"
                   href={`https://www.google.com/maps/search/?api=1&query=${o.delivery_location.lat},${o.delivery_location.lng}`}>
                  {o.delivery_location.lat.toFixed(5)}, {o.delivery_location.lng.toFixed(5)} ↗
                </a>
              ) : "—"}
            </KeyRow>
            <KeyRow label="Zone">{o.zone}</KeyRow>
          </Panel>

          <SharePanel orderId={o.id} />
          <Panel title="Buyer">
            <KeyRow label="Email"><CustomerLink email={o.buyer_email} /></KeyRow>
            <KeyRow label="Newsletter"><AddToNewsletter email={o.buyer_email} testid="order-add-to-newsletter" /></KeyRow>
            <KeyRow label="Phone">{o.buyer_phone}</KeyRow>
            <KeyRow label="Country">{o.buyer_country}</KeyRow>
            {o.subscription ? (
              <KeyRow label="Recurring plan">
                <Link className="underline decoration-brass" to={`/admin/subscriptions?q=${o.subscription.subscription_number}`}>
                  {o.subscription.subscription_number} · {o.subscription.plan}
                </Link>
              </KeyRow>
            ) : null}
          </Panel>

          <Panel title="Shop" testid="order-drawer-shop">
            <KeyRow label="Assigned to"><ShopLink florist={o.florist} /></KeyRow>
            <KeyRow label="Phone">{o.florist?.phone}</KeyRow>
            {o.acceptance ? (
              <KeyRow label="Acceptance clock">
                <span className={o.acceptance_overdue ? "text-danger font-medium" : "text-ink"} data-testid="order-drawer-acceptance-clock">
                  {o.acceptance.working_minutes} / {o.acceptance.limit_minutes} working min
                  {o.acceptance_overdue ? " · OVERDUE" : " · waiting"}
                </span>
              </KeyRow>
            ) : null}
            {o.proof?.photo_urls?.length ? (
              <ZoomImage src={media(o.proof.photo_urls[0])} alt="Delivery proof" caption="Delivery photo"
                         className="w-48 aspect-[4/5]" testid="order-drawer-proof" />
            ) : null}
            {o.proof && !o.proof.photo_urls?.length ? (
              <KeyRow label="Delivery note">
                <span data-testid="order-drawer-proof-note">{o.proof.florist_note || "Delivered without a photo"}</span>
              </KeyRow>
            ) : null}
          </Panel>

          {o.status === "awaiting_acceptance" ? <HandOverPanel orderId={o.id} overdue={o.acceptance_overdue} act={act} /> : null}

          <Panel title="Operations">
            <div className="flex flex-wrap gap-2 items-center">
              <select className="ctl" data-testid="order-drawer-reassign"
                      onChange={(e) => e.target.value && act(() => api.post(`/admin/orders/${o.id}/reassign`, { florist_id: e.target.value, reason: "ops reassignment" }), "Handed over.")}>
                <option value="">Hand to another partner…</option>
                {shops.filter((s) => s.city_id === o.city_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <button className="btn-ghost text-[11px]" data-testid="order-drawer-reroute" onClick={() => act(() => api.post(`/admin/orders/${o.id}/reroute`), "Routing again.")}>Route again</button>
              <button className="btn-ghost text-[11px]" data-testid="order-drawer-refund" onClick={() => act(() => api.post(`/admin/orders/${o.id}/refund`, { reason: "goodwill" }), "Refunded.")}>Refund in full</button>
              <button className="btn-ghost text-[11px]" data-testid="order-drawer-cancel" onClick={() => act(() => api.post(`/admin/orders/${o.id}/cancel`, { note: "cancelled by ops" }), "Cancelled.")}>Cancel</button>
              <button className="btn-ghost text-[11px]" data-testid="order-drawer-resolve" onClick={() => act(() => api.post(`/admin/orders/${o.id}/resolve-issue`, { note: "resolved with buyer" }), "Issue closed.")}>Resolve issue</button>
            </div>
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); act(() => api.post(`/admin/orders/${o.id}/note`, { note }), "Note added."); setNote(""); }}>
              <input className="field flex-1" placeholder="Add a note to the timeline" value={note} data-testid="order-drawer-note" onChange={(e) => setNote(e.target.value)} />
              <button className="btn-ghost text-xs">Save</button>
            </form>
            {o.issues?.length ? <p className="text-xs text-danger">Issues: {o.issues.map((i) => i.type).join(", ")}</p> : null}
          </Panel>
        </div>
      ) : null}

      {tab === "payment" ? (
        <div className="space-y-4" data-testid="order-drawer-payment">
          <Panel title="Gateway">
            <KeyRow label="Gateway"><Badge tone="ink">{p.gateway}</Badge> {p.mode}</KeyRow>
            <KeyRow label="Capture">{p.capture_mode} · hold {p.payment_state}</KeyRow>
            <KeyRow label="Gateway id">{p.payment_id || "—"}</KeyRow>
            <KeyRow label="Session">{p.session_id || "—"}</KeyRow>
            {p.dashboard_url ? (
              <a className="btn-ghost text-xs inline-flex items-center gap-1" href={p.dashboard_url} target="_blank" rel="noreferrer" data-testid="order-drawer-gateway-link">
                Open in {p.gateway} <ExternalLink size={12} />
              </a>
            ) : null}
            {p.transaction_id ? (
              <Link className="btn-ghost text-xs" to={`/admin/payments?payment=${p.transaction_id}`} data-testid="order-drawer-payment-link">See the transaction</Link>
            ) : null}
          </Panel>
          <MoneyActions o={o} act={act} />
          <Panel title="The money" testid="order-drawer-money">
            {o.money ? (
              <>
                <KeyRow label="Bouquet list price">{money(o.money.list_subtotal_mad)} MAD{o.money.shop_sale_mad ? ` · shop sale −${money(o.money.shop_sale_mad)}` : ""}</KeyRow>
                <KeyRow label="Bouquet sold for">{money(o.money.subtotal_mad)} MAD</KeyRow>
                <KeyRow label="Extras">{o.money.addons_mad ? `${money(o.money.addons_mad)} MAD (partner cost ${money(o.money.addons_cost_mad)})` : "—"}</KeyRow>
                <KeyRow label="Delivery fee (Fleurinelle)">{money(o.money.delivery_fee_mad)} MAD</KeyRow>
                <KeyRow label="Discount">{o.money.discount_mad ? `−${money(o.money.discount_mad)} MAD · ${o.money.discount_code || o.money.promotion_name || o.money.discount_label || ""}` : "—"}</KeyRow>
                <KeyRow label="Order total">{money(o.money.total_mad)} MAD · charged {o.money.display_currency} {o.money.total_charged} (fx {o.money.fx_rate})</KeyRow>
                <KeyRow label="Partner cost price">{money(o.money.cost_mad)} MAD <span className="text-brass tabular" data-testid="order-cost-eur">{eurOf(fx, o.money.cost_mad)}</span></KeyRow>
                <KeyRow label="Card fee">{money(o.money.gateway_fee_mad)} MAD{o.money.gateway_fee_florist_mad ? ` · partner share ${money(o.money.gateway_fee_florist_mad)}` : " · Fleurinelle absorbs it"}</KeyRow>
                <KeyRow label="Partner payout">{money(o.money.payout_mad)} MAD <span className="text-brass tabular" data-testid="order-payout-eur">{eurOf(fx, o.money.payout_mad)}</span></KeyRow>
                <KeyRow label="Fleurinelle keeps">{money(o.money.platform_net_mad)} MAD</KeyRow>
                <KeyRow label="Reported to ad platforms">{o.money.conversion_value} {o.money.conversion_currency}</KeyRow>
                <KeyRow label="Refunded">{o.money.refund_mad ? `${money(o.money.refund_mad)} MAD · ${p.refund_reason || ""}` : "—"}</KeyRow>
                <KeyRow label="Payout status">{p.payout_id ? "included in a weekly statement" : "not paid out yet"}</KeyRow>
              </>
            ) : (
              <KeyRow label="Charged">{o.currency} {o.total_charged} · {money(p.total_mad)} MAD</KeyRow>
            )}
          </Panel>
          {o.items_detail?.length ? (
            <Panel title="Line by line" testid="order-drawer-lines">
              {o.items_detail.map((i, idx) => (
                <KeyRow key={`item-${idx}`} label={`${i.quantity} × ${i.title}`}>
                  {i.variant} · sold {money(i.unit_price_mad)} MAD · cost {money(i.unit_cost_mad)} MAD
                  {i.sale_percent ? ` · shop sale −${i.sale_percent}%` : ""}
                </KeyRow>
              ))}
              {(o.addons_detail || []).map((a, idx) => (
                <KeyRow key={`addon-${idx}`} label={`${a.quantity} × ${a.name}`}>
                  {money(a.unit_price_mad)} MAD · {a.fulfilled_by === "partner" ? `partner paid ${money(a.unit_cost_mad)} MAD` : "Fleurinelle fulfils"}
                  {a.added_after_payment ? " · added after payment" : ""}
                </KeyRow>
              ))}
            </Panel>
          ) : null}
          {o.topups?.length ? (
            <Panel title="Extras bought after payment" testid="order-drawer-topups">
              {o.topups.map((tu) => (
                <KeyRow key={tu.id} label={stamp(tu.created_at)}>
                  <Badge tone={tu.status === "paid" ? "good" : "warn"}>{tu.status}</Badge>{" "}
                  {tu.amount_charged} {tu.currency} · {money(tu.amount_mad)} MAD · {tu.items.map((i) => `${i.quantity} × ${i.name?.en || ""}`).join(", ")}
                </KeyRow>
              ))}
            </Panel>
          ) : null}
          {o.buyer ? (
            <Panel title="Buyer & consent trail" testid="order-drawer-buyer">
              <KeyRow label="E-mail">{o.buyer.email}</KeyRow>
              <KeyRow label="Phone">{o.buyer.phone || "—"} · {o.buyer.country} · {o.buyer.locale}</KeyRow>
              <KeyRow label="Recipient WhatsApp">{o.buyer.recipient_whatsapp || "—"}</KeyRow>
              <KeyRow label="Call before delivery">{o.buyer.call_before_delivery ? "yes" : "surprise"}</KeyRow>
              <KeyRow label="WhatsApp updates">{o.buyer.whatsapp_opt_in ? "opted in" : "no"}</KeyRow>
              <KeyRow label="Came from an ad">
                {[o.buyer.ad_click?.meta ? "Meta click id" : null, o.buyer.ad_click?.chatgpt ? "ChatGPT Ads click id" : null]
                  .filter(Boolean).join(" · ") || "no click id"}
              </KeyRow>
              <KeyRow label="Landed on">{o.buyer.source_url || "—"}</KeyRow>
            </Panel>
          ) : null}
          <Panel title="Gateway events">
            {o.gateway_events?.length ? o.gateway_events.map((e, i) => (
              <p key={`${e.received_at}-${e.type || e.event_type}-${i}`} className="text-xs border-b border-brass-light py-1 flex gap-2">
                <span className="tabular text-muted">{stamp(e.received_at)}</span><span>{e.type || e.event_type}</span>
              </p>
            )) : <p className="text-sm text-muted">No webhook has mentioned this order yet.</p>}
          </Panel>
        </div>
      ) : null}

      {tab === "timeline" ? (
        <ol className="space-y-2 text-sm" data-testid="order-drawer-timeline">
          {o.events?.map((e) => (
            <li key={e.id} className="flex gap-3 border-b border-brass-light py-1.5">
              <span className="tabular text-muted whitespace-nowrap">{stamp(e.created_at)}</span>
              <span><Badge tone={statusTone(e.status)}>{e.status}</Badge> <span className="text-muted">{e.actor_type}</span> {e.note}</span>
            </li>
          ))}
        </ol>
      ) : null}

      {tab === "messages" ? (
        <div className="space-y-1 text-sm" data-testid="order-drawer-messages">
          {o.messages?.length ? o.messages.map((m, i) => (
            <p key={`${m.created_at}-${m.to}-${i}`} className="flex gap-3 border-b border-brass-light py-1.5">
              <span className="tabular text-muted whitespace-nowrap">{stamp(m.created_at)}</span>
              <Badge tone={m.success ? "good" : "bad"}>{m.channel}</Badge>
              <span>{m.kind} → {m.to}</span>
              <span className="ms-auto text-xs text-muted">{(m.detail || "").slice(0, 40)}</span>
            </p>
          )) : <p className="text-muted">Nothing sent for this order.</p>}
        </div>
      ) : null}
    </Drawer>
  );
}
